import React, { useState } from 'react';
import { base44 } from '@/api/base44Client';
import { Card, CardHeader, CardTitle, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Loader2, Sparkles, ChevronDown, ChevronRight, CheckCircle, XCircle, AlertTriangle, Link2, FileText, Zap } from 'lucide-react';
import { Collapsible, CollapsibleContent, CollapsibleTrigger } from "@/components/ui/collapsible";
import { Progress } from "@/components/ui/progress";

/**
 * Small button to trigger enrichment on a single issue.
 * Use inside IssueCard or Phase2 triage list.
 */
export function IssueEnrichButton({ issue, onEnriched }) {
  const [loading, setLoading] = useState(false);

  const handleEnrich = async (e) => {
    e?.stopPropagation();
    if (issue.ai_enrichment) return; // already enriched
    setLoading(true);
    try {
      const response = await base44.functions.invoke('councilIssueEnrich', { issue_id: issue.id });
      const data = response.data || response;
      onEnriched?.(issue.id, data.enrichment);
    } catch (err) {
      console.error('Enrichment failed:', err);
    } finally {
      setLoading(false);
    }
  };

  if (issue.ai_enrichment) {
    return (
      <Badge className="bg-purple-500/20 text-purple-300 text-[10px] cursor-default">
        <Sparkles className="w-3 h-3 mr-1" /> AI Enriched
      </Badge>
    );
  }

  return (
    <Button
      size="sm"
      variant="ghost"
      onClick={handleEnrich}
      disabled={loading}
      className="h-6 text-[10px] text-purple-400 hover:text-purple-300 hover:bg-purple-500/10"
    >
      {loading
        ? <Loader2 className="w-3 h-3 animate-spin mr-1" />
        : <Sparkles className="w-3 h-3 mr-1" />
      }
      {loading ? 'Enriching...' : 'AI Enrich'}
    </Button>
  );
}

/**
 * Display panel showing enrichment results for a single issue.
 * Use inside an expanded IssueCard or detail view.
 */
export function IssueEnrichmentDisplay({ enrichment }) {
  const [expanded, setExpanded] = useState(false);

  if (!enrichment) return null;

  const gate1Tests = enrichment.gate1_test_predictions;
  const testLabels = {
    repeatability: 'Repeatability',
    impact_magnitude: 'Impact Magnitude',
    causal_clarity: 'Causal Clarity',
    breadth_of_impact: 'Breadth of Impact',
    escalation_trend: 'Escalation Trend',
    strategic_relevance: 'Strategic Relevance'
  };

  const passCount = gate1Tests
    ? Object.values(gate1Tests).filter(Boolean).length
    : null;

  return (
    <div className="space-y-2 p-3 rounded-lg bg-purple-950/20 border border-purple-500/10">
      {/* Key Insight */}
      {enrichment.key_insight && (
        <p className="text-xs text-purple-200 font-medium">{enrichment.key_insight}</p>
      )}

      {/* Gate Predictions */}
      <div className="flex flex-wrap gap-2">
        <Badge className={enrichment.gate1_prediction === 'signal'
          ? 'bg-amber-500/20 text-amber-300'
          : 'bg-slate-500/20 text-slate-300'
        }>
          Gate 1: {enrichment.gate1_prediction} ({enrichment.gate1_confidence})
        </Badge>
        {enrichment.gate2_prediction && enrichment.gate2_prediction !== 'unknown' && (
          <Badge className={enrichment.gate2_prediction === 'rock'
            ? 'bg-stone-600/20 text-stone-300'
            : 'bg-sky-500/20 text-sky-300'
          }>
            Gate 2: {enrichment.gate2_prediction} ({enrichment.gate2_confidence})
          </Badge>
        )}
        {passCount !== null && (
          <Badge className="bg-purple-500/20 text-purple-300">
            Signal Tests: {passCount}/6
          </Badge>
        )}
      </div>

      {/* Expandable Details */}
      <Collapsible open={expanded} onOpenChange={setExpanded}>
        <CollapsibleTrigger className="flex items-center gap-1 text-[10px] text-purple-400 hover:text-purple-300 cursor-pointer">
          {expanded ? <ChevronDown className="w-3 h-3" /> : <ChevronRight className="w-3 h-3" />}
          {expanded ? 'Hide details' : 'Show details'}
        </CollapsibleTrigger>
        <CollapsibleContent>
          <div className="mt-2 space-y-3">
            {/* Signal Detection Tests */}
            {gate1Tests && (
              <div className="space-y-1">
                <p className="text-[10px] font-semibold text-purple-400 uppercase tracking-wider">Signal Detection Tests</p>
                <div className="grid grid-cols-2 gap-1">
                  {Object.entries(testLabels).map(([key, label]) => (
                    <div key={key} className="flex items-center gap-1.5 text-[10px]">
                      {gate1Tests[key]
                        ? <CheckCircle className="w-3 h-3 text-emerald-400" />
                        : <XCircle className="w-3 h-3 text-slate-600" />
                      }
                      <span className={gate1Tests[key] ? 'text-slate-200' : 'text-slate-500'}>{label}</span>
                    </div>
                  ))}
                </div>
              </div>
            )}

            {/* Root Cause */}
            {enrichment.root_cause_hypothesis && (
              <div>
                <p className="text-[10px] font-semibold text-purple-400 uppercase tracking-wider">Root Cause Hypothesis</p>
                <p className="text-xs text-slate-300 mt-0.5">{enrichment.root_cause_hypothesis}</p>
              </div>
            )}

            {/* Recommended Actions */}
            {Array.isArray(enrichment.recommended_actions) && enrichment.recommended_actions.length > 0 && (
              <div>
                <p className="text-[10px] font-semibold text-purple-400 uppercase tracking-wider">Recommended Actions</p>
                <div className="space-y-0.5 mt-0.5">
                  {enrichment.recommended_actions.map((action, idx) => (
                    <p key={idx} className="text-xs text-slate-300 flex items-start gap-1">
                      <span className="text-purple-400 flex-shrink-0">→</span> {action}
                    </p>
                  ))}
                </div>
              </div>
            )}

            {/* Similar Issues */}
            {Array.isArray(enrichment.similar_issues) && enrichment.similar_issues.length > 0 && (
              <div>
                <p className="text-[10px] font-semibold text-purple-400 uppercase tracking-wider flex items-center gap-1">
                  <Link2 className="w-3 h-3" /> Similar Issues
                </p>
                <div className="space-y-0.5 mt-0.5">
                  {enrichment.similar_issues.map((si, idx) => (
                    <p key={idx} className="text-[10px] text-slate-400">
                      <span className="text-slate-300">#{si.issue_id}</span> — {si.relevance}
                    </p>
                  ))}
                </div>
              </div>
            )}

            {/* Related Policies */}
            {Array.isArray(enrichment.related_policies) && enrichment.related_policies.length > 0 && (
              <div>
                <p className="text-[10px] font-semibold text-purple-400 uppercase tracking-wider flex items-center gap-1">
                  <FileText className="w-3 h-3" /> Related Policies
                </p>
                <div className="space-y-0.5 mt-0.5">
                  {enrichment.related_policies.map((rp, idx) => (
                    <p key={idx} className="text-[10px] text-slate-400">
                      <span className="text-slate-300">Policy #{rp.policy_id}</span> — {rp.relevance}
                    </p>
                  ))}
                </div>
              </div>
            )}

            {/* Related Capabilities */}
            {Array.isArray(enrichment.related_capabilities) && enrichment.related_capabilities.length > 0 && (
              <div>
                <p className="text-[10px] font-semibold text-purple-400 uppercase tracking-wider flex items-center gap-1">
                  <Zap className="w-3 h-3" /> Related Capabilities
                </p>
                <div className="space-y-0.5 mt-0.5">
                  {enrichment.related_capabilities.map((rc, idx) => (
                    <p key={idx} className="text-[10px] text-slate-400">
                      <span className="text-slate-300">Cap #{rc.capability_id}</span> — {rc.relevance}
                    </p>
                  ))}
                </div>
              </div>
            )}
          </div>
        </CollapsibleContent>
      </Collapsible>
    </div>
  );
}

/**
 * Bulk enrichment component for Phase 1.
 * Sequentially enriches all un-enriched open issues with a progress bar.
 */
export default function BulkIssueEnrichment({ issues, onComplete }) {
  const [running, setRunning] = useState(false);
  const [progress, setProgress] = useState({ current: 0, total: 0, results: [] });

  const unenriched = issues.filter(i =>
    !i.ai_enrichment && ['open', 'under_review'].includes(i.status)
  );

  const handleBulkEnrich = async () => {
    if (unenriched.length === 0) return;
    setRunning(true);
    const total = unenriched.length;
    setProgress({ current: 0, total, results: [] });

    const results = [];
    for (let idx = 0; idx < total; idx++) {
      const issue = unenriched[idx];
      try {
        const response = await base44.functions.invoke('councilIssueEnrich', { issue_id: issue.id });
        const data = response.data || response;
        results.push({ issue_id: issue.id, success: true, enrichment: data.enrichment });
      } catch (err) {
        console.error(`Enrichment failed for issue ${issue.id}:`, err);
        results.push({ issue_id: issue.id, success: false, error: err.message });
      }
      setProgress({ current: idx + 1, total, results: [...results] });
      // Small delay between calls to avoid rate limiting
      if (idx < total - 1) {
        await new Promise(r => setTimeout(r, 1000));
      }
    }

    setRunning(false);
    onComplete?.(results);
  };

  const enrichedCount = issues.filter(i => i.ai_enrichment).length;
  const totalOpen = issues.filter(i => ['open', 'under_review'].includes(i.status)).length;
  const pct = progress.total > 0 ? Math.round((progress.current / progress.total) * 100) : 0;

  return (
    <Card className="glass-panel border-purple-500/20">
      <CardHeader className="pb-2">
        <CardTitle className="text-sm text-purple-400 flex items-center gap-2">
          <Sparkles className="w-4 h-4" /> AI Issue Enrichment
        </CardTitle>
      </CardHeader>
      <CardContent className="space-y-3">
        <div className="flex items-center justify-between text-xs text-slate-400">
          <span>Enriched: {enrichedCount}/{totalOpen} open issues</span>
          <span>{unenriched.length} remaining</span>
        </div>

        {running ? (
          <div className="space-y-2">
            <Progress value={pct} className="h-2" />
            <div className="flex items-center justify-between text-xs">
              <span className="text-purple-300 flex items-center gap-1">
                <Loader2 className="w-3 h-3 animate-spin" />
                Enriching issue {progress.current}/{progress.total}...
              </span>
              <span className="text-slate-500">{pct}%</span>
            </div>
            {progress.results.length > 0 && (
              <div className="space-y-0.5 max-h-32 overflow-y-auto">
                {progress.results.map((r, idx) => (
                  <div key={idx} className="flex items-center gap-1 text-[10px]">
                    {r.success
                      ? <CheckCircle className="w-3 h-3 text-emerald-400" />
                      : <AlertTriangle className="w-3 h-3 text-red-400" />
                    }
                    <span className={r.success ? 'text-slate-300' : 'text-red-300'}>
                      Issue #{r.issue_id} {r.success ? '— enriched' : `— failed: ${r.error}`}
                    </span>
                  </div>
                ))}
              </div>
            )}
          </div>
        ) : (
          <Button
            onClick={handleBulkEnrich}
            disabled={unenriched.length === 0}
            className="w-full bg-purple-600 hover:bg-purple-700 text-white h-9 text-sm disabled:opacity-50"
          >
            <Sparkles className="w-4 h-4 mr-2" />
            {unenriched.length === 0
              ? 'All Issues Enriched'
              : `Enrich ${unenriched.length} Issue${unenriched.length !== 1 ? 's' : ''}`
            }
          </Button>
        )}
      </CardContent>
    </Card>
  );
}